import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ContextService } from './context.service';
import { map, Observable } from 'rxjs';
import {
  Octopus,
  WorkflowActionProperty,
  WorkflowActionSearch,
} from '@maximizer/core/shared/domain';

@Injectable()
export class WorkflowActionService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  private getQuery(
    params?: WorkflowActionSearch,
  ):
    | Octopus.SearchQuery<Octopus.WorkflowAction>
    | Octopus.LogicalQuery<Octopus.WorkflowAction> {
    const searchArray = [];
    if (params?.key) {
      searchArray.push({
        Key: {
          $EQ: params.key,
        },
      });
    }
    if (params?.action) {
      searchArray.push({
        Action: {
          $EQ: params.action,
        },
      });
    }
    if (params?.parentWorkflowKey) {
      searchArray.push({
        ParentWorkflowKey: {
          $EQ: params.parentWorkflowKey,
        },
      });
    }

    if (searchArray.length === 1) {
      return searchArray[0];
    } else if (searchArray.length > 1) {
      return {
        $AND: searchArray,
      };
    }

    return {};
  }

  read(param?: WorkflowActionSearch): Observable<WorkflowActionProperty[]> {
    const searchQuery = this.getQuery(param);
    const request: Octopus.WorkflowActionReadRequest = {
      WorkflowAction: {
        Scope: {
          Fields: {
            Key: 1,
            ParentWorkflowKey: 1,
            Action: 1,
            Conditions: 1,
            Operations: 1,
          },
        },
        Criteria: {
          SearchQuery: searchQuery,
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowActionReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowActionMapper();
            return mapper.from(result.WorkflowAction.Data);
          }
          return [];
        }),
      );
  }

  update(values: WorkflowActionProperty): Observable<string | null> {
    const request = {
      WorkflowAction: {
        Data: {
          Key: values.key ?? null,
          ParentWorkflowKey: values.parentWorkflowKey,
          Action: values.action,
          Conditions: values.conditions ?? undefined,
          Operations:
            values.operations?.map((op) => ({
              Type: op.type,
              Data: op.data,
              FieldName: op.fieldName ?? null,
            })) ?? undefined,
        },
      },
    };
    return this.http
      .post<Octopus.WorkflowActionWriteResponse>(
        `${this.context.api}${values.key ? Octopus.Action.UPDATE : Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.WorkflowAction.Data.Key;
          }
          return null;
        }),
      );
  }

  delete(key: string): Observable<boolean> {
    const request = {
      WorkflowAction: {
        Data: {
          Key: key,
        },
      },
    };
    return this.http
      .post<Octopus.WorkflowActionWriteResponse>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
      )
      .pipe(
        map((result) => {
          return result.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }
}
